import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Business,
  Security,
  Support,
  LocalShipping,
  Verified,
  TrendingUp,
  Star,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const AboutUsPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const values = [
    {
      icon: <Security />,
      title: 'Trust & Security',
      description: 'We prioritize the security of your data and transactions with industry-leading encryption and security measures.',
    },
    {
      icon: <Support />,
      title: 'Customer Support',
      description: 'Our dedicated customer support team is available to assist you with any queries or concerns.',
    },
    {
      icon: <LocalShipping />,
      title: 'Reliable Delivery',
      description: 'We ensure reliable delivery of your orders with our trusted logistics partners.',
    },
    {
      icon: <Verified />,
      title: 'Quality Assurance',
      description: 'Every product on our platform goes through quality checks to ensure you get the best value.',
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="h2"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 'bold',
                textAlign: 'center',
                fontSize: { xs: '2.5rem', md: '3.5rem' },
              }}
            >
              About Our Platform
            </Typography>
            <Typography
              variant="h5"
              sx={{
                textAlign: 'center',
                opacity: 0.9,
                maxWidth: '800px',
                mx: 'auto',
                lineHeight: 1.6,
                fontSize: { xs: '1.1rem', md: '1.3rem' },
              }}
            >
              We are committed to providing a seamless marketplace experience
              that connects buyers and sellers in a secure, efficient environment.
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Mission Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <Typography
                variant="h3"
                component="h2"
                gutterBottom
                sx={{ fontWeight: 'bold', color: 'primary.main' }}
              >
                Our Mission
              </Typography>
              <Typography
                variant="body1"
                sx={{ fontSize: '1.1rem', lineHeight: 1.8, mb: 3 }}
              >
                We strive to create a trusted marketplace that empowers businesses
                and individuals to connect, trade, and grow together. Our platform
                is designed to facilitate seamless transactions while maintaining
                the highest standards of security and user experience.
              </Typography>
              <Typography
                variant="body1"
                sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}
              >
                Through innovative technology and dedicated service, we aim to
                bridge the gap between buyers and sellers, creating value for
                all participants in our ecosystem.
              </Typography>
            </motion.div>
          </Grid>
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <Box
                sx={{
                  height: 300,
                  background: 'linear-gradient(45deg, #f0f2f5 0%, #e1e5e9 100%)',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Business sx={{ fontSize: 120, color: 'primary.main', opacity: 0.7 }} />
              </Box>
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      {/* Values Section */}
      <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            component="h2"
            gutterBottom
            sx={{ textAlign: 'center', fontWeight: 'bold', mb: 6 }}
          >
            Our Core Values
          </Typography>
          <Grid container spacing={4}>
            {values.map((value, index) => (
              <Grid item xs={12} md={6} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card
                    elevation={2}
                    sx={{
                      height: '100%',
                      transition: 'transform 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: 6,
                      },
                    }}
                  >
                    <CardContent sx={{ p: 4 }}>
                      <Avatar
                        sx={{
                          bgcolor: 'primary.main',
                          width: 60,
                          height: 60,
                          mb: 3,
                        }}
                      >
                        {value.icon}
                      </Avatar>
                      <Typography variant="h5" fontWeight="bold" gutterBottom>
                        {value.title}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" lineHeight={1.7}>
                        {value.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Vision Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Typography
            variant="h3"
            component="h2"
            gutterBottom
            sx={{ textAlign: 'center', fontWeight: 'bold', mb: 4 }}
          >
            Our Vision
          </Typography>
          <Typography
            variant="h6"
            sx={{
              textAlign: 'center',
              maxWidth: '800px',
              mx: 'auto',
              lineHeight: 1.8,
              color: 'text.secondary',
            }}
          >
            To become the leading marketplace platform that enables businesses
            of all sizes to thrive in the digital economy. We envision a future
            where commerce is accessible, transparent, and beneficial for everyone
            involved in the trading process.
          </Typography>
        </motion.div>
      </Container>

      {/* Contact CTA Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: 8,
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Typography
              variant="h3"
              component="h2"
              gutterBottom
              sx={{ textAlign: 'center', fontWeight: 'bold' }}
            >
              Join Our Platform
            </Typography>
            <Typography
              variant="h6"
              sx={{
                textAlign: 'center',
                opacity: 0.9,
                maxWidth: '600px',
                mx: 'auto',
                mb: 4,
              }}
            >
              Whether you're looking to buy quality products or sell your goods,
              our platform provides the tools and support you need to succeed.
            </Typography>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default AboutUsPage;